import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/base/sheet";
import { DataTable } from "@/components/ui/data-table/DataTable";
import { termSplit, toCsv, toCsvFilename } from "@/application/signals/cohort";
import type {
  Cohort,
  MerchantSignalRow,
  SignalsAggregates,
} from "@/application/signals/types";
import { downloadTextFile } from "@/lib/download";
import {
  formatCount,
  formatCurrency,
  formatDate,
  formatDays,
  formatStallStage,
  formatTerm,
} from "../format";

/**
 * The drill-through: the merchants behind whichever slice was clicked, and the
 * export that turns that slice into a working list.
 *
 * Rows carry no names — the artifact holds no PII — so `businessId` links out
 * to the merchant record for anyone who needs the identity behind a row.
 */
export function CohortPanel({
  cohort,
  rows,
  mccBySegment,
  generatedAt,
  onClose,
  onDrill,
}: {
  cohort: Cohort | null;
  rows: MerchantSignalRow[];
  mccBySegment: SignalsAggregates["mccBySegment"];
  generatedAt: string;
  onClose: () => void;
  onDrill: (cohort: Cohort) => void;
}) {
  const navigate = useNavigate();

  // The raw codes behind a curated segment, so the taxonomy stays inspectable:
  // a segment that looks wrong can be opened to see which MCCs put it there.
  const segment = cohort?.filter.segment;
  const mccCodes =
    segment && cohort?.filter.mccCode === undefined
      ? (new Map(Object.entries(mccBySegment)).get(segment) ?? [])
      : [];

  // A Pro slice is read by billing term, so it gets the per-row term and the
  // split up front rather than leaving it to be counted off the table.
  const isPro = cohort?.filter.plan === "pro";
  const split = useMemo(() => (isPro ? termSplit(rows) : null), [isPro, rows]);

  const columns: ColumnDef<MerchantSignalRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "businessId",
        header: "Business",
        cell: ({ row }) => (
          <button
            type="button"
            className="cursor-pointer font-mono text-xs text-blue-600 hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              navigate(
                ROUTES.MERCHANT_DETAIL.replace(
                  ":businessId",
                  row.original.businessId,
                ),
              );
            }}
          >
            {row.original.businessId.slice(0, 8)}
          </button>
        ),
      },
      ...(isPro
        ? [
            {
              accessorKey: "subscriptionTerm",
              header: "Term",
              cell: ({ row }) => {
                const term = row.original.subscriptionTerm;
                return term === "monthly" || term === "annual" ? (
                  formatTerm(term)
                ) : (
                  <span className="text-muted-foreground italic">
                    {formatTerm(term)}
                  </span>
                );
              },
            } satisfies ColumnDef<MerchantSignalRow, unknown>,
          ]
        : []),
      { accessorKey: "segmentLabel", header: "Segment" },
      { accessorKey: "country", header: "Country" },
      {
        accessorKey: "signupAt",
        header: "Signed up",
        cell: ({ row }) => formatDate(row.original.signupAt),
      },
      {
        accessorKey: "activated",
        header: "Status",
        cell: ({ row }) =>
          row.original.activated ? (
            <span className="text-emerald-600">Activated</span>
          ) : (
            <span className="text-muted-foreground">
              {row.original.stallStage
                ? formatStallStage(row.original.stallStage)
                : "Not activated"}
            </span>
          ),
      },
      {
        accessorKey: "daysToFirstTxn",
        header: "To 1st txn",
        cell: ({ row }) => formatDays(row.original.daysToFirstTxn),
      },
      {
        accessorKey: "txnCount",
        header: "Txns",
        cell: ({ row }) => formatCount(row.original.txnCount),
      },
      {
        accessorKey: "gpvCents",
        header: "GPV",
        cell: ({ row }) =>
          row.original.currency
            ? formatCurrency(row.original.gpvCents, row.original.currency)
            : "—",
      },
    ],
    [navigate, isPro],
  );

  return (
    <Sheet open={cohort !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-4xl">
        <SheetHeader>
          <SheetTitle>{cohort?.label ?? "Cohort"}</SheetTitle>
          <SheetDescription>
            {formatCount(rows.length)} merchants in the Amplify base
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
          {split && (
            <dl
              className="grid grid-cols-3 gap-3"
              aria-label="Pro subscriptions by term"
            >
              {(
                [
                  ["Monthly", split.monthly],
                  ["Annual", split.annual],
                  ["Unknown", split.unknown],
                ] as const
              ).map(([label, count]) => (
                <div key={label} className="rounded-lg border px-3 py-2">
                  <dt className="text-muted-foreground text-xs font-medium">
                    {label}
                  </dt>
                  <dd className="text-foreground text-lg font-semibold tabular-nums">
                    {formatCount(count)}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          <Button
            variant="outline"
            size="sm"
            className="self-start"
            disabled={rows.length === 0}
            onClick={() =>
              downloadTextFile(
                toCsvFilename(cohort?.label ?? "cohort", generatedAt),
                toCsv(rows),
              )
            }
          >
            <Download className="mr-1.5 h-4 w-4" />
            Export CSV
          </Button>

          {mccCodes.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1.5 text-xs font-medium">
                Merchant category codes in this segment
              </p>
              <div className="flex flex-wrap gap-1.5">
                {mccCodes.map((entry) => (
                  <button
                    key={entry.mccCode}
                    type="button"
                    className="border-border text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted cursor-pointer rounded-full border px-2.5 py-1 text-xs"
                    onClick={() =>
                      onDrill({
                        label: `${cohort?.label ?? "Segment"} · MCC ${entry.mccCode}`,
                        filter: { segment, mccCode: entry.mccCode },
                      })
                    }
                  >
                    {entry.mccCode}
                    <span className="text-subtle-foreground ml-1.5 tabular-nums">
                      {formatCount(entry.merchants)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {rows.length === 0 ? (
            <p className="text-subtle-foreground py-8 text-center text-sm">
              No merchants in this slice
            </p>
          ) : (
            <DataTable columns={columns} data={rows} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
